import type { ReactNode } from 'react';
import { useUiLabels, UiLink } from '../labels';
import { cn } from '../lib/cn';
import { useIsPhone } from '../lib/use-media-query';
import { Banner } from './banner';
import { Button } from './button';
import { Checkbox } from './choice';
import { Icon } from './icon';
import { Pagination, type PaginationProps } from './pagination';
import { Skeleton } from './skeleton';

const widths = { xs: 'w-16', sm: 'w-28', md: 'w-44', lg: 'w-64', auto: '' };

export interface Column<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  sortable?: boolean;
  /** Numbers: right aligned with tabular digits. */
  numeric?: boolean;
  width?: keyof typeof widths;
  /** Hidden on tablets, e.g. secondary dates. */
  desktopOnly?: boolean;
  /** Hides the header text, e.g. for a column of row actions. */
  hideHeader?: boolean;
}

export interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

/** Card layout on the phone: which columns go where (docs/ui/responsive.md). */
export interface MobileLayout {
  title: string;
  subtitle?: string;
  /** Shown as small label/value lines below. */
  fields?: readonly string[];
  /** Right side of the card, e.g. status or row actions. */
  trailing?: string;
}

export interface DataTableProps<T> {
  /** Accessible name of the table. */
  caption: string;
  columns: readonly Column<T>[];
  rows: readonly T[] | undefined;
  rowKey: (row: T) => string;
  /** Name of a row for screen readers (selection checkbox). */
  rowLabel?: (row: T) => string;
  /** Column whose content becomes the link or button of the row. Default: the first column. */
  primaryColumn?: string;
  /** Makes the row a link. */
  rowHref?: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  error?: { message: ReactNode; onRetry?: () => void };
  empty?: ReactNode;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  selection?: { selected: ReadonlySet<string>; onChange: (selected: Set<string>) => void };
  mobile?: MobileLayout;
  pagination?: PaginationProps;
  /** Below the list, e.g. a button that loads more rows. */
  footer?: ReactNode;
  className?: string;
}

/**
 * List of records: table on tablet and desktop, cards on the phone. Loading, empty and error
 * states are built in, so every list behaves the same (docs/ui/design-system.md).
 */
export function DataTable<T>(props: DataTableProps<T>) {
  const phone = useIsPhone();
  const { rows, loading, error, empty, pagination, footer, className } = props;

  let body: ReactNode;
  if (error) {
    body = <TableError {...error} />;
  } else if (!loading && rows && rows.length === 0) {
    body = <div className="border-t border-line md:border-t-0">{empty}</div>;
  } else {
    body = phone ? <CardList {...props} /> : <Table {...props} />;
  }

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="overflow-hidden rounded-lg border border-line bg-surface">{body}</div>
      {pagination && !error && <Pagination {...pagination} />}
      {footer && !error && <div className="flex justify-center pt-4">{footer}</div>}
    </div>
  );
}

function TableError({ message, onRetry }: { message: ReactNode; onRetry?: () => void }) {
  const labels = useUiLabels();
  return (
    <div className="p-4">
      <Banner
        tone="danger"
        role="alert"
        action={
          onRetry && (
            <Button size="sm" icon="refresh-cw" onClick={onRetry}>
              {labels.retry}
            </Button>
          )
        }
      >
        {message}
      </Banner>
    </div>
  );
}

function PrimaryCell<T>({
  row,
  content,
  rowHref,
  onRowClick,
}: {
  row: T;
  content: ReactNode;
  rowHref?: (row: T) => string;
  onRowClick?: (row: T) => void;
}) {
  // The pseudo element stretches the link over the whole row; other controls sit above it.
  const classes =
    'rounded-sm font-medium text-fg text-left after:absolute after:inset-0 hover:underline focus-visible:after:outline-2 focus-visible:after:outline-brand focus-visible:outline-none';
  if (rowHref) {
    return (
      <UiLink href={rowHref(row)} className={classes}>
        {content}
      </UiLink>
    );
  }
  if (onRowClick) {
    return (
      <button
        type="button"
        className={cn(classes, 'cursor-pointer')}
        onClick={() => onRowClick(row)}
      >
        {content}
      </button>
    );
  }
  return <>{content}</>;
}

function SelectAll<T>({
  rows,
  rowKey,
  selection,
}: Pick<DataTableProps<T>, 'rows' | 'rowKey' | 'selection'>) {
  const labels = useUiLabels();
  if (!selection) return null;
  const keys = (rows ?? []).map(rowKey);
  const count = keys.filter((key) => selection.selected.has(key)).length;
  const checked = count === 0 ? false : count === keys.length ? true : 'indeterminate';
  return (
    <Checkbox
      aria-label={labels.selectAll}
      checked={checked}
      disabled={keys.length === 0}
      onCheckedChange={(value) => {
        const next = new Set(selection.selected);
        for (const key of keys) {
          if (value === true) next.add(key);
          else next.delete(key);
        }
        selection.onChange(next);
      }}
    />
  );
}

function SelectRow<T>({
  row,
  rowKey,
  rowLabel,
  selection,
}: Pick<DataTableProps<T>, 'rowKey' | 'rowLabel' | 'selection'> & { row: T }) {
  const labels = useUiLabels();
  if (!selection) return null;
  const key = rowKey(row);
  return (
    <Checkbox
      aria-label={rowLabel ? rowLabel(row) : labels.selectRow}
      checked={selection.selected.has(key)}
      className="relative z-10"
      onCheckedChange={(value) => {
        const next = new Set(selection.selected);
        if (value === true) next.add(key);
        else next.delete(key);
        selection.onChange(next);
      }}
    />
  );
}

function SortButton({
  column,
  sort,
  onSortChange,
}: {
  column: Column<unknown>;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
}) {
  const labels = useUiLabels();
  const active = sort?.column === column.id;
  const next: SortState = {
    column: column.id,
    direction: active && sort.direction === 'asc' ? 'desc' : 'asc',
  };
  return (
    <button
      type="button"
      onClick={() => onSortChange?.(next)}
      title={next.direction === 'asc' ? labels.sortAscending : labels.sortDescending}
      className={cn(
        '-mx-1.5 inline-flex cursor-pointer items-center gap-1 rounded-sm px-1.5 py-0.5 hover:bg-hover hover:text-fg',
        column.numeric && 'flex-row-reverse',
        active && 'text-fg',
      )}
    >
      {column.header}
      <Icon
        name={active ? (sort.direction === 'asc' ? 'arrow-up' : 'arrow-down') : 'arrow-up-down'}
        className={cn('size-3.5', !active && 'text-fg-disabled')}
      />
    </button>
  );
}

function Table<T>(props: DataTableProps<T>) {
  const labels = useUiLabels();
  const { caption, columns, rows, rowKey, loading, sort, onSortChange, selection } = props;
  const primary = props.primaryColumn ?? columns[0]?.id;
  const interactive = !!(props.rowHref || props.onRowClick);
  const cellClasses = (column: Column<T>) =>
    cn(
      'px-3 first:pl-4 last:pr-4',
      widths[column.width ?? 'auto'],
      column.numeric && 'text-right tabular',
      column.desktopOnly && 'hidden lg:table-cell',
    );

  return (
    // Positioned, so the row links stay inside the table where the browser does not position
    // table rows (older Safari); otherwise they would cover the whole window.
    <div className="relative overflow-x-auto">
      <table className="w-full border-collapse text-sm" aria-busy={loading || undefined}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-line bg-canvas">
            {selection && (
              <th scope="col" className="w-10 pl-4">
                <SelectAll {...props} />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                aria-sort={
                  sort?.column === column.id
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
                className={cn(
                  cellClasses(column),
                  'h-9 text-left text-xs font-medium whitespace-nowrap text-fg-muted',
                  column.numeric && 'text-right',
                )}
              >
                {column.hideHeader ? (
                  <span className="sr-only">{column.header}</span>
                ) : column.sortable ? (
                  <SortButton
                    column={column as Column<unknown>}
                    sort={sort}
                    onSortChange={onSortChange}
                  />
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && !rows
            ? Array.from({ length: 5 }, (_, index) => (
                <tr key={index} className="border-b border-line last:border-b-0">
                  {selection && <td className="h-12 pl-4" />}
                  {columns.map((column) => (
                    <td key={column.id} className={cn(cellClasses(column), 'h-12')}>
                      <Skeleton
                        className={cn('h-3.5', column.numeric ? 'ml-auto w-12' : 'w-3/4')}
                      />
                    </td>
                  ))}
                </tr>
              ))
            : (rows ?? []).map((row) => {
                const key = rowKey(row);
                const selected = selection?.selected.has(key);
                return (
                  <tr
                    key={key}
                    aria-selected={selection ? selected : undefined}
                    className={cn(
                      'relative border-b border-line transition-colors transition-fast last:border-b-0',
                      interactive && 'hover:bg-hover',
                      selected && 'bg-brand-subtle',
                    )}
                  >
                    {selection && (
                      <td className="w-10 pl-4">
                        <SelectRow row={row} {...props} />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={column.id}
                        className={cn(cellClasses(column), 'h-12 py-2 text-fg-muted')}
                      >
                        {column.id === primary ? (
                          <PrimaryCell
                            row={row}
                            content={column.cell(row)}
                            rowHref={props.rowHref}
                            onRowClick={props.onRowClick}
                          />
                        ) : (
                          <div
                            className={cn(
                              'relative z-10 w-fit max-w-full',
                              column.numeric && 'ml-auto',
                            )}
                          >
                            {column.cell(row)}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
        </tbody>
      </table>
      {loading && rows && (
        <span className="sr-only" role="status">
          {labels.loading}
        </span>
      )}
    </div>
  );
}

function CardList<T>(props: DataTableProps<T>) {
  const { caption, columns, rows, rowKey, loading, selection } = props;
  const byId = new Map(columns.map((column) => [column.id, column]));
  const layout: MobileLayout = props.mobile ?? {
    title: columns[0]?.id ?? '',
    fields: columns.slice(1).map((column) => column.id),
  };
  const render = (id: string | undefined, row: T) => (id ? byId.get(id)?.cell(row) : null);

  if (loading && !rows) {
    return (
      <ul aria-label={caption} aria-busy className="divide-y divide-line">
        {Array.from({ length: 4 }, (_, index) => (
          <li key={index} className="flex flex-col gap-2 p-4">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul aria-label={caption} className="divide-y divide-line">
      {(rows ?? []).map((row) => {
        const key = rowKey(row);
        return (
          <li
            key={key}
            className={cn(
              'relative flex items-start gap-3 p-4 transition-colors transition-fast active:bg-pressed',
              selection?.selected.has(key) && 'bg-brand-subtle',
            )}
          >
            {selection && (
              <span className="flex h-5 items-center">
                <SelectRow row={row} {...props} />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-base">
                <PrimaryCell
                  row={row}
                  content={render(layout.title, row)}
                  rowHref={props.rowHref}
                  onRowClick={props.onRowClick}
                />
              </div>
              {layout.subtitle && (
                <div className="mt-0.5 truncate text-fg-muted">{render(layout.subtitle, row)}</div>
              )}
              {layout.fields && layout.fields.length > 0 && (
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  {layout.fields.map((id) => {
                    const column = byId.get(id);
                    if (!column) return null;
                    return (
                      <div key={id} className="contents">
                        <dt className="text-fg-subtle">{column.header}</dt>
                        <dd className={cn('min-w-0 text-fg-muted', column.numeric && 'tabular')}>
                          {column.cell(row)}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              )}
            </div>
            {layout.trailing && (
              <div className="relative z-10 shrink-0">{render(layout.trailing, row)}</div>
            )}
            {(props.rowHref || props.onRowClick) && !layout.trailing && (
              <Icon name="chevron-right" className="mt-1 text-fg-subtle" />
            )}
          </li>
        );
      })}
    </ul>
  );
}
